import React, { RefObject, useCallback } from 'react';

import { menuBannerColors } from '../../../helpers';

import { Preview } from '../preview';
import { PreviewData } from '../preview.types';

import { NavBar } from '../navbar';
import { MenuBanner } from './menu-banner';
import { MenuImageBanner } from './menu-image-banner';
import { Carousels } from './carousels';
import { Disclaimer } from './disclaimer';
import { CategoriesV2 } from './categories-v2';

type HomePageProps = {
  asMobileApp?: boolean;
  data: PreviewData;
  activeSection?: 'categories' | 'disclaimer' | 'homepage' | 'textBanner';
};

type UseHomePageReturn = {
  menuBannerBackgroundColor: string;
};

function useHomePage(data: PreviewData): UseHomePageReturn {
  const menuBannerBackgroundColor = menuBannerColors[data.menuBannerColor].background;

  return {
    menuBannerBackgroundColor,
  };
}

export function HomePage({ asMobileApp, data, activeSection }: HomePageProps): JSX.Element {
  const { menuBannerBackgroundColor } = useHomePage(data);
  const {
    menuBannerHtml,
    disclaimerTextHtml,
    menuSections,
    categoriesV2,
    imageBanners,
    menuBannerTextBannerCollapsed,
  } = data;

  const scrollIntoView = useCallback((componentRef: RefObject<HTMLElement>): void => {
    const previouslyFocused = document.activeElement;
    componentRef.current?.focus();

    if (previouslyFocused instanceof HTMLElement) {
      previouslyFocused.focus();
    }
  }, []);

  return (
    <Preview asMobileApp={asMobileApp} data={data}>
      {!asMobileApp && <NavBar />}

      {!!imageBanners?.length && <MenuImageBanner banners={imageBanners} />}

      {menuBannerHtml && (
        <MenuBanner
          bannerColor={menuBannerBackgroundColor}
          bannerHtml={menuBannerHtml}
          bannerCollapsed={menuBannerTextBannerCollapsed}
          shouldScrollInView={activeSection === 'textBanner'}
          scrollIntoView={scrollIntoView}
        />
      )}

      <CategoriesV2 settings={categoriesV2} />

      <Carousels
        menuSections={menuSections}
        flairColor={asMobileApp ? data.colors.buttonsLinks : undefined}
        asMobileApp={asMobileApp}
        buttonFlair={asMobileApp ? data.mobile?.buttonFlair ?? undefined : undefined}
      />

      {disclaimerTextHtml && <Disclaimer disclaimerHtml={disclaimerTextHtml} />}
    </Preview>
  );
}
